/*
 * <acs-carousel> — controller for ACS scroll-snap carousels.
 * ---------------------------------------------------------------------------
 * A progressive enhancement over a native `scroll-snap-type: x mandatory`
 * track. The track is the source of truth: the active index is *derived* from
 * scrollLeft (rAF-throttled) rather than stored, so a thumb swipe, a mouse
 * wheel, a keypress and a programmatic scroll all converge on the same state.
 * With this file blocked, the markup is still a swipeable, arrow-key-scrollable
 * region — the stylesheet keeps the controls hidden until the element upgrades
 * (`acs-carousel:not(:defined)`), so nothing dead is shown and no content is
 * hidden behind JS.
 *
 * Section-agnostic on purpose: every lookup is scoped to the element via
 * generic data attributes, there is no module state, and any number of
 * instances can coexist on a page. Reuse it from another section by shipping
 * the same markup contract:
 *
 *   <acs-carousel data-autoplay="true" data-interval="5000"
 *                 data-state="playing"
 *                 data-label-play="…" data-label-pause="…">
 *     <div data-acs-carousel-track tabindex="0" role="group" aria-label="…">
 *       <article data-acs-carousel-slide id="…">…</article>
 *     </div>
 *     <button data-acs-carousel-prev>…</button>
 *     <button data-acs-carousel-next>…</button>
 *     <button data-acs-carousel-dot aria-current="true">…</button>
 *     <button data-acs-carousel-toggle>…</button>   <!-- pause/play, WCAG 2.2.2 -->
 *   </acs-carousel>
 *
 * Everything except the track and two slides is optional.
 *
 * Wrapped in an IIFE and guarded on customElements.get: two instances of the
 * same section (or a second carousel section) each emit this <script src>, and
 * duplicate <script> elements with one src still execute once *per element*.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  if (typeof window.customElements === 'undefined' || window.customElements.get('acs-carousel')) return;

  var SNAP_RESTORE_MS = 900;
  var MIN_INTERVAL_MS = 2000;
  var DEFAULT_INTERVAL_MS = 5000;

  class AcsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.track = this.scope('[data-acs-carousel-track]')[0];
      this.slides = this.track ? this.scope('[data-acs-carousel-slide]') : [];

      // One slide is a static figure, not a carousel — leave it entirely alone.
      if (!this.track || this.slides.length < 2) return;
      this.initialised = true;

      this.dots = this.scope('[data-acs-carousel-dot]');
      this.prevButton = this.scope('[data-acs-carousel-prev]')[0];
      this.nextButton = this.scope('[data-acs-carousel-next]')[0];
      this.toggleButton = this.scope('[data-acs-carousel-toggle]')[0];

      this.index = 0;
      this.frame = 0;
      this.timer = 0;
      this.snapTimer = 0;
      this.hovering = false;
      this.dragging = false;
      this.focusWithin = false;
      this.inView = true;

      var interval = parseInt(this.dataset.interval, 10);
      this.interval = isNaN(interval) ? DEFAULT_INTERVAL_MS : Math.max(MIN_INTERVAL_MS, interval);

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Reduced-motion visitors start paused; pressing play is an explicit
      // opt-in, and their moves stay instant rather than smooth.
      this.playing = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;

      this.listen();
      this.index = this.activeIndexFromScroll();
      this.render();
      this.syncTimer();
    }

    disconnectedCallback() {
      this.teardown();
      // Let the theme editor move/re-insert the section and re-wire cleanly.
      this.initialised = false;
    }

    /* --- plumbing ------------------------------------------------------- */

    /** Descendants that belong to THIS element, so nesting can never cross-wire. */
    scope(selector) {
      var self = this;
      return Array.prototype.filter.call(this.querySelectorAll(selector), function (element) {
        return element.closest('acs-carousel') === self;
      });
    }

    listen() {
      this.controller = new AbortController();
      var signal = this.controller.signal;
      var self = this;

      this.track.addEventListener('scroll', function () { self.onScroll(); }, { passive: true, signal: signal });

      // Restore snapping the moment the scroll settles, so a long glide across
      // several cards is never cut short by the fallback timeout below.
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', function () { self.restoreSnap(); }, { signal: signal });
      }

      this.track.addEventListener('keydown', function (event) { self.onKeydown(event); }, { signal: signal });
      this.track.addEventListener('click', function (event) { self.onTrackClick(event); }, { signal: signal });

      // Auto-advance must not fight the visitor: gate it off while they are
      // hovering, dragging, tabbed inside, or looking at another tab.
      this.addEventListener('pointerenter', function () { self.hovering = true; self.syncTimer(); }, { signal: signal });
      this.addEventListener('pointerleave', function () {
        self.hovering = false;
        self.dragging = false;
        self.syncTimer();
      }, { signal: signal });
      this.addEventListener('pointerdown', function () { self.dragging = true; self.syncTimer(); }, { signal: signal });
      this.addEventListener('pointerup', function () { self.dragging = false; self.syncTimer(); }, { signal: signal });
      this.addEventListener('pointercancel', function () { self.dragging = false; self.syncTimer(); }, { signal: signal });
      this.addEventListener('focusin', function () { self.focusWithin = true; self.syncTimer(); }, { signal: signal });
      this.addEventListener('focusout', function (event) {
        // relatedTarget is where focus is heading; document.activeElement is
        // still mid-transition at this point and would read as <body>.
        self.focusWithin = !!(event.relatedTarget && self.contains(event.relatedTarget));
        self.syncTimer();
      }, { signal: signal });

      document.addEventListener('visibilitychange', function () { self.syncTimer(); }, { signal: signal });

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', function (event) {
          if (event.matches) self.playing = false;
          self.render();
          self.syncTimer();
        }, { signal: signal });
      }

      if (this.prevButton) {
        this.prevButton.addEventListener('click', function () { self.goTo(self.index - 1); }, { signal: signal });
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', function () { self.goTo(self.index + 1); }, { signal: signal });
      }
      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', function () {
          self.playing = !self.playing;
          self.render();
          self.syncTimer();
        }, { signal: signal });
      }
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { self.goTo(i); }, { signal: signal });
      });

      if (typeof window.IntersectionObserver === 'function') {
        this.observer = new IntersectionObserver(function (entries) {
          self.inView = entries.some(function (entry) { return entry.isIntersecting; });
          self.syncTimer();
        }, { threshold: 0.2 });
        this.observer.observe(this);
      }
    }

    teardown() {
      if (this.controller) this.controller.abort();
      if (this.observer) this.observer.disconnect();
      if (this.frame) window.cancelAnimationFrame(this.frame);
      window.clearTimeout(this.snapTimer);
      window.clearInterval(this.timer);
      this.frame = 0;
      this.timer = 0;
      this.snapTimer = 0;
    }

    /* --- state ---------------------------------------------------------- */

    onScroll() {
      var self = this;
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(function () {
        self.frame = 0;
        var next = self.activeIndexFromScroll();
        if (next === self.index) return;
        self.index = next;
        self.render();
      });
    }

    /** Distance of each slide's centre from the scrollport centre wins. */
    activeIndexFromScroll() {
      var track = this.track;
      var origin = track.getBoundingClientRect().left - track.scrollLeft;
      var centre = track.scrollLeft + track.clientWidth / 2;
      var best = 0;
      var bestDistance = Infinity;

      this.slides.forEach(function (slide, i) {
        var rect = slide.getBoundingClientRect();
        var slideCentre = rect.left - origin + rect.width / 2;
        var distance = Math.abs(slideCentre - centre);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });

      return best;
    }

    goTo(index) {
      var track = this.track;
      var total = this.slides.length;
      var target = ((index % total) + total) % total;
      var slide = this.slides[target];
      if (!slide) return;

      var rect = slide.getBoundingClientRect();
      var left =
        track.scrollLeft + (rect.left - track.getBoundingClientRect().left) - (track.clientWidth - rect.width) / 2;

      if (this.reducedMotion.matches) {
        track.scrollTo({ left: left, behavior: 'auto' });
      } else {
        // Mandatory snap hijacks a smooth programmatic scroll at the first
        // snap point it crosses, so suspend it for the flight and restore it
        // on scrollend — native touch snapping is untouched in the meantime.
        var self = this;
        track.style.scrollSnapType = 'none';
        window.clearTimeout(this.snapTimer);
        this.snapTimer = window.setTimeout(function () { self.restoreSnap(); }, SNAP_RESTORE_MS);
        track.scrollTo({ left: left, behavior: 'smooth' });
      }

      // Paint the new state immediately; the scroll handler confirms it.
      this.index = target;
      this.render();
    }

    /** Idempotent: hand the track back to CSS snapping. */
    restoreSnap() {
      window.clearTimeout(this.snapTimer);
      this.snapTimer = 0;
      if (this.track.style.scrollSnapType) this.track.style.scrollSnapType = '';
    }

    render() {
      var index = this.index;

      this.dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      this.dataset.state = this.playing ? 'playing' : 'paused';

      if (this.toggleButton) {
        var label = this.playing ? this.dataset.labelPause : this.dataset.labelPlay;
        if (label) this.toggleButton.setAttribute('aria-label', label);
      }
    }

    /** The single place that decides whether the deck is allowed to move itself. */
    syncTimer() {
      window.clearInterval(this.timer);
      this.timer = 0;

      var allowed =
        this.playing && !this.hovering && !this.dragging && !this.focusWithin && this.inView && !document.hidden;
      if (!allowed) return;

      var self = this;
      this.timer = window.setInterval(function () {
        self.goTo(self.index + 1);
      }, this.interval);
    }

    /* --- input ---------------------------------------------------------- */

    onKeydown(event) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      switch (event.key) {
        case 'ArrowRight':
          this.goTo(this.index + 1);
          break;
        case 'ArrowLeft':
          this.goTo(this.index - 1);
          break;
        case 'Home':
          this.goTo(0);
          break;
        case 'End':
          this.goTo(this.slides.length - 1);
          break;
        default:
          return;
      }

      event.preventDefault();
    }

    /** Tap a slide peeking at the edge to bring it to the centre. */
    onTrackClick(event) {
      if (event.target.closest('a, button, [role="button"], input, label, summary')) return;

      var selection = window.getSelection();
      if (selection && selection.type === 'Range' && !selection.isCollapsed) return;

      var slide = event.target.closest('[data-acs-carousel-slide]');
      if (!slide) return;

      var target = this.slides.indexOf(slide);
      if (target === -1 || target === this.index) return;

      this.goTo(target);
    }
  }

  window.customElements.define('acs-carousel', AcsCarousel);
})();
